import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Country from '../../../types/country/Country';
import CountryWriteDTO from '../../../types/country/CountryWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class CountryService {

    private RESOURCE: string = 'country';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the countries from the backend.
     *
     * @param search The search query to filter the countries by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The countries.
     */
    public async getCountries(search?: string, page: number = 0, size: number = 10): Promise<Page<Country>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<Country>>(this.RESOURCE, params);
    }

    /**
     * Fetches all countries from the backend.
     *
     * @return The countries.
     */
    public async getAllCountries(): Promise<Country[]> {
        return await this.baseService.get<Country[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a country from the backend.
     *
     * @param id The ID of the country to fetch.
     * @return The country.
     */
    public async getCountry(id: number): Promise<Country> {
        return await this.baseService.get<Country>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a country in the backend.
     *
     * @param country The country to create.
     * @return The response from the backend.
     */
    public async createCountry(country: CountryWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, country);
    }

    /**
     * Updates a country in the backend.
     *
     * @param country The country to update.
     * @return The response from the backend.
     */
    public async updateCountry(country: CountryWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + country.id, country);
    }

    /**
     * Deletes a country from the backend.
     *
     * @param id The ID of the country to delete.
     * @return The response from the backend.
     */
    public async deleteCountry(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
